import { IconButton } from '@mui/material';
import {
  GridColDef,
  getGridDateOperators,
  getGridStringOperators,
} from '@mui/x-data-grid-pro';
import moment from 'moment';

import { IcDotSpread } from '@/assets/images';
import { excludeValues } from '@/components/DataGrid/utils';
import { DATE_TIME_FORMAT } from '@/config';
import {
  IOffenseOption,
  IOffenseOptionItem,
  PropertyStatus,
  PropertyStatusList,
} from '@/models';
import {
  getOffenseOptionItems,
  returnCleanValue,
} from '@/pages/Kases/KaseCrimes/utilities';
import { getUpdatedLabel } from '@/utils';

const number = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const typeTag = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const itemNumber = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const name = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const loggedInfo = (): Partial<GridColDef> => ({
  field: 'loggedInfo.loggedDate',
  renderCell: (params) =>
    params.row.loggedInfo?.loggedDate
      ? moment(params.row.loggedInfo.loggedDate).format(DATE_TIME_FORMAT)
      : '/',
  valueGetter: (_value, row) => {
    const raw = row.loggedInfo?.loggedDate;
    return raw ? moment(raw) : null;
  },
  sortComparator: (a, b) => {
    const dateA = a?.toDate?.();
    const dateB = b?.toDate?.();

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateA.getTime() - dateB.getTime();
  },
  filterOperators: getGridDateOperators(true),
});

const involvement = (
  filteredOptions?: IOffenseOption[],
): Partial<GridColDef> => {
  if (!filteredOptions || !filteredOptions.length) {
    return {};
  }

  const PropertyInvolvementList = getOffenseOptionItems(
    'involvement',
    filteredOptions,
  ) as Map<string, IOffenseOptionItem>;

  return {
    field: 'involvement',
    renderCell: (params) => {
      return returnCleanValue(
        PropertyInvolvementList?.get(params.row.involvement)?.label,
      );
    },
    filterable: false,
  };
};

const type = (filteredOptions?: IOffenseOption[]): Partial<GridColDef> => {
  const PropertyType = getOffenseOptionItems(
    'propertyType',
    filteredOptions || [],
  ) as Map<string, IOffenseOptionItem>;
  return {
    renderCell: (params) =>
      params.row.type
        ? [...PropertyType.values()].find(
            (value) => value.key === params.row.type,
          )?.label
        : '/',
    filterable: false,
    valueGetter: (_, row) =>
      [...PropertyType.values()].find((value) => value.key === row.type)?.label,
  };
};

const status = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    params.row.status
      ? PropertyStatusList.get(params.row.status as PropertyStatus)
      : '/',
  filterable: false,
});

const lastUpdated = (): Partial<GridColDef> => ({
  renderCell: (params) => getUpdatedLabel(params.row?.updatedAt),
  sortable: false,
  filterable: false,
});

const actions = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: any) => void,
): Partial<GridColDef> => ({
  width: 96,
  sortable: false,
  filterable: false,
  align: 'center',
  headerAlign: 'center',
  disableColumnMenu: true,
  renderCell: (params) => (
    <IconButton
      onClick={(event) => {
        event.stopPropagation();
        openMenu?.(event, params.row);
      }}
    >
      <IcDotSpread />
    </IconButton>
  ),
});

export const masterPropertyCols = (
  _navigate?: (path: string) => void,
  filteredOptions?: IOffenseOption[],
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: any) => void,
) => {
  return {
    number,
    name,
    typeTag,
    itemNumber,
    loggedInfo,
    type: () => type(filteredOptions),
    involvement: () => involvement(filteredOptions),
    status,
    lastUpdated,
    actions: () => actions(openMenu),
  };
};
